import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { Locale } from "../shared/dto";
import type { StaffMatrixClient, StaffRun } from "./staff/model";
import { isTestTenant } from "../shared/test-tenant";
import { TestFilterNote } from "./staff/TestFilterNote";
import { AdminReloadRow } from "./staff/AdminReload";
import { intlLanguage } from "./date-format";
import { DETAIL_CONFIG_METADATA, hasImportDetail, importDetailCodes } from "../shared/stripe-import-detail";
import "./stripe-config-source.css";
import { profileResearchDiagnosticKey } from "./profile-research-diagnostics";

// Admin panel for the Stripe purchase import: master switch, product mappings,
// webhook event log, and the profile research queue. Mutations go through the
// shared StaffRun (cookie + CSRF + idempotency); reads are same-origin GETs.

interface PanelProps {
  t: (key: string) => string;
  locale: Locale;
  run: StaffRun;
  clients: StaffMatrixClient[];
  hideTestTenants: boolean;
  onHideTestTenantsChange: (hide: boolean) => void;
}

interface ImportSettings {
  enabled: boolean;
  defaultPartnerId: string;
  /** F168: partner for Empire + existing Buddy; "" = default partner rule. */
  empireExistingPartnerId: string;
  /** F171: partner for every "Jen prodej" purchase; "" = default partner rule. */
  salesOnlyPartnerId?: string;
}

interface MappingRow {
  id: string;
  stripe_product_id: string;
  label: string;
  market_language: Locale;
  includes_app: number;
  active: number;
}

interface EventRow {
  id: string;
  is_test?: boolean;
  stripe_event_type: string;
  customer_email: string | null;
  customer_name: string | null;
  matched_product_label: string | null;
  outcome: string;
  detail: string | null;
  client_id: string | null;
  client_name: string | null;
  created_at: string;
}

interface JobRow {
  id: string;
  client_id: string;
  client_name: string;
  /** Server verdict over name AND e-mail, the only way the check is complete. */
  is_test: boolean;
  source: string;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
}

interface PartnerRow {
  id: string;
  display_name: string | null;
  email: string;
}

interface OverviewResponse {
  ok: boolean;
  settings: ImportSettings;
  mappings: MappingRow[];
  events: EventRow[];
  jobs: JobRow[];
  partners: PartnerRow[];
}

/**
 * A product mapping and a Stripe product are not tenants: they have a name and
 * no address at all, so the name is the ONLY signal there is. Named, because
 * the same call written inline as `isTestTenant(x, "")` is what hid the S-04
 * bug on the client rows right below - there the address existed and was
 * simply not passed.
 */
function isTestLabel(label: string): boolean {
  return isTestTenant(label, "");
}

const EMPTY_MANUAL_IMPORT = {
  name: "", email: "", marketLanguage: "", journeyMode: "", includesApp: true, partnerId: ""
};

const OUTCOME_KEYS = ["imported", "switch_off", "no_match", "already_client", "deferred_unpaid", "error"];
const JOB_STATUS_KEYS = ["pending", "running", "done", "failed", "canceled"];

/**
 * One internal code, one sentence. The metadata family comes first because it
 * is the one an operator can act on directly: it names the KEY on the Stripe
 * product that has to change (S2-07 - nothing internal reaches the screen, and
 * a refusal names its own way out).
 */
function eventIssueKey(code: string): string | null {
  const prefix = "staffConsole.stripeImport.eventIssue.";
  if (code === "metadata_flag") return `${prefix}metadataFlag`;
  if (code === "metadata_label") return `${prefix}metadataLabel`;
  if (code === "metadata_language") return `${prefix}metadataLanguage`;
  if (code === "metadata_app") return `${prefix}metadataApp`;
  if (code === "metadata_source") return `${prefix}metadataSource`;
  if (code === "metadata_start") return `${prefix}metadataStart`;
  if (code === "metadata_payments") return `${prefix}metadataPayments`;
  // F153: the productType dialect names its own keys, so the next step stays
  // "fix it at the product" and the sentence says WHICH key.
  if (code === "metadata_product_type") return `${prefix}metadataProductType`;
  if (code === "metadata_product_language") return `${prefix}metadataProductLanguage`;
  if (code === "metadata_product_app") return `${prefix}metadataProductApp`;
  if (code === "metadata_dialects_disagree") return `${prefix}metadataDialectsDisagree`;
  if (code === "missing_default_partner") return `${prefix}missingPartner`;
  // F139's stale-shortcut note, which until F148 fell through to the generic
  // "import se nepodařilo dokončit" - a failure sentence on an imported client.
  if (code === "default_partner_unavailable") return `${prefix}stalePartner`;
  // F168: the Empire-with-existing-Buddy shortcut went stale the same way.
  if (code === "empire_partner_unavailable") return `${prefix}staleEmpirePartner`;
  // F171: the "Jen prodej" partner went stale the same way.
  if (code === "sales_only_partner_unavailable") return `${prefix}staleSalesOnlyPartner`;
  if (code === "missing_email") return `${prefix}missingEmail`;
  if (code === "no_products_in_session") return `${prefix}noProducts`;
  if (code === "research_job_skipped") return `${prefix}profileAlreadyQueued`;
  if (code === "session_replay") return `${prefix}repeatedPurchase`;
  if (code.startsWith("stripe_api_")) return `${prefix}stripeConnection`;
  if (code.startsWith("create_")) return `${prefix}clientCreation`;
  // The configuration channel has its own line and is not an issue.
  if (code === DETAIL_CONFIG_METADATA) return null;
  return null;
}

export function StripeImportPanel(props: PanelProps) {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [empirePartnerId, setEmpirePartnerId] = useState("");
  const [salesOnlyPartnerId, setSalesOnlyPartnerId] = useState("");
  // F116: the language starts UNCHOSEN. It used to be pre-filled with the
  // ADMIN's own interface language, so a Czech admin mapping an American
  // product created "cs" without touching the field - and from that one
  // silent value the client's whole dashboard, e-mails and generated
  // materials follow. An unchosen language refuses the submit and says so.
  const [mapping, setMapping] = useState({ stripeProductId: "", label: "", marketLanguage: "", includesApp: true });
  const [products, setProducts] = useState<Array<{ id: string; name: string }> | null>(null);
  const [productsUnavailable, setProductsUnavailable] = useState(false);
  const [researchClientId, setResearchClientId] = useState("");
  // The hand-made client is the same decision as the mapping above it, so it
  // starts unchosen for the same reason: `props.locale` is the ADMIN's own
  // interface language, and every client a Czech admin created by hand became
  // Czech without anybody stating it.
  // F152-W1 (D11): the journey starts UNCHOSEN, like the market language beside
  // it. This form recovers a purchase whose product metadata the webhook could
  // not read, so which half was bought is precisely the thing nobody here
  // knows - and the F116 law is that a silent value from which the client's
  // whole journey follows may not be inherited from a default. It deliberately
  // differs from the admin creation form next door, which preselects `full`:
  // there the axis is one field of a client somebody is inventing and the
  // editor is one click away, here it is the one fact the missed webhook took
  // with it.
  const [manual, setManual] = useState(EMPTY_MANUAL_IMPORT);
  const [notice, setNotice] = useState("");
  const visibleClients = props.hideTestTenants
    ? props.clients.filter((client) => !client.is_test)
    : props.clients;
  const visiblePartners = props.hideTestTenants
    ? (data?.partners ?? []).filter((partner) => !isTestTenant(partner.display_name ?? "", partner.email))
    : data?.partners ?? [];
  const visibleProducts = props.hideTestTenants
    ? products?.filter((product) => !isTestLabel(product.name))
    : products;

  const reload = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/stripe-import", { credentials: "same-origin" });
      if (!response.ok) { setLoadError(true); return; }
      const body = await response.json() as OverviewResponse;
      setData(body);
      setLoadError(false);
      setEnabled(body.settings.enabled);
      setPartnerId(body.settings.defaultPartnerId);
      setEmpirePartnerId(body.settings.empireExistingPartnerId ?? "");
      setSalesOnlyPartnerId(body.settings.salesOnlyPartnerId ?? "");
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    if (researchClientId && !visibleClients.some((client) => client.id === researchClientId)) {
      setResearchClientId("");
    }
  }, [researchClientId, visibleClients]);
  useEffect(() => {
    if (partnerId && !visiblePartners.some((partner) => partner.id === partnerId)) setPartnerId("");
    if (empirePartnerId && !visiblePartners.some((partner) => partner.id === empirePartnerId)) setEmpirePartnerId("");
    if (salesOnlyPartnerId && !visiblePartners.some((partner) => partner.id === salesOnlyPartnerId)) setSalesOnlyPartnerId("");
    if (manual.partnerId && !visiblePartners.some((partner) => partner.id === manual.partnerId)) {
      setManual((current) => ({ ...current, partnerId: "" }));
    }
    if (mapping.stripeProductId && !visibleProducts?.some((product) => product.id === mapping.stripeProductId)) {
      setMapping((current) => ({ ...current, stripeProductId: "", label: "" }));
    }
  }, [data, empirePartnerId, salesOnlyPartnerId, manual.partnerId, mapping.stripeProductId, partnerId, products, props.hideTestTenants]);

  async function saveSettings(event: FormEvent) {
    event.preventDefault();
    setNotice("");
    const response = await props.run("/api/admin/stripe-import/settings", {
      method: "PUT",
      body: { enabled, defaultPartnerId: partnerId, empireExistingPartnerId: empirePartnerId, salesOnlyPartnerId }
    }, { refresh: false });
    if (response.ok) { setNotice(props.t("staffConsole.stripeImport.saved")); void reload(); }
  }

  async function addMapping(event: FormEvent) {
    event.preventDefault();
    const response = await props.run("/api/admin/stripe-import/mappings", {
      method: "POST",
      body: mapping
    }, { refresh: false });
    if (response.ok) {
      setMapping({ stripeProductId: "", label: "", marketLanguage: "", includesApp: true });
      void reload();
    }
  }

  async function toggleMapping(row: MappingRow) {
    const response = await props.run(`/api/admin/stripe-import/mappings/${row.id}`, {
      method: "PATCH",
      body: { active: row.active !== 1 }
    }, { refresh: false });
    if (response.ok) void reload();
  }

  async function loadProducts() {
    setProductsUnavailable(false);
    try {
      const response = await fetch("/api/admin/stripe-import/products", { credentials: "same-origin" });
      if (!response.ok) { setProductsUnavailable(true); return; }
      const body = await response.json() as { products?: Array<{ id: string; name: string }> };
      setProducts(body.products ?? []);
    } catch {
      setProductsUnavailable(true);
    }
  }

  // Manual import: the same outcome as a Stripe purchase (client + welcome
  // login link + profile research), composed from the existing admin
  // endpoints. Works independently of the master switch.
  async function importManually(event: FormEvent) {
    event.preventDefault();
    setNotice("");
    const partnerId = manual.partnerId || data?.settings.defaultPartnerId || "";
    const created = await props.run("/api/admin/clients", {
      method: "POST",
      body: {
        name: manual.name.trim(),
        ownerEmail: manual.email.trim(),
        ownerDisplayName: manual.name.trim(),
        marketLanguage: manual.marketLanguage,
        purchaseSource: "direct",
        instanceStartMode: "new",
        // F152-W1: the axis the operator STATED. It was hard-coded, under a
        // comment that called every purchase a full journey - true the day F143
        // wrote it, false the day "Jen prodej" went on sale. A missed webhook
        // for a sales_only purchase recovered here would have built the wrong
        // journey, and `journeyModeOf` only guards the opposite direction.
        journeyMode: manual.journeyMode,
        partnerId,
        includesApp: manual.includesApp
      }
    }, { refresh: false });
    if (!created.ok) {
      setNotice(props.t("staffConsole.stripeImport.manualImportFailed"));
      return;
    }
    const body = await created.json() as { clientId?: string };
    if (body.clientId) {
      await props.run(`/api/admin/clients/${body.clientId}/research`, { method: "POST", body: {} }, { refresh: false });
    }
    setNotice(props.t("staffConsole.stripeImport.manualImportDone"));
    setManual(EMPTY_MANUAL_IMPORT);
    void reload();
  }

  async function runResearch(event: FormEvent) {
    event.preventDefault();
    setNotice("");
    const response = await props.run(`/api/admin/clients/${researchClientId.trim()}/research`, {
      method: "POST",
      body: {}
    }, { refresh: false });
    if (response.ok) {
      setNotice(props.t("staffConsole.stripeImport.researchQueued"));
      setResearchClientId("");
      void reload();
    } else if (response.status === 409) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      if (body?.error === "dossier_curated") {
        setNotice(props.t("staffConsole.stripeImport.researchCurated"));
      }
    }
  }

  const outcomeLabel = (outcome: string) =>
    OUTCOME_KEYS.includes(outcome)
      ? props.t(`staffConsole.stripeImport.outcome.${outcome}`)
      : props.t("staffConsole.stripeImport.outcome.unknown");
  const jobStatusLabel = (status: string) =>
    JOB_STATUS_KEYS.includes(status)
      ? props.t(`staffConsole.stripeImport.jobStatus.${status}`)
      : props.t("staffConsole.stripeImport.jobStatus.unknown");
  const researchFailureMessage = (lastError: string | null) => {
    if (!lastError) return null;
    const diagnostic = profileResearchDiagnosticKey(lastError);
    if (diagnostic) return props.t(diagnostic);
    const normalized = lastError.toLowerCase();
    if (normalized.includes("facts_count") || normalized.includes("fact_count")) {
      return props.t("staffConsole.stripeImport.failure.notEnoughFacts");
    }
    if (normalized.includes("timeout") || normalized.includes("network")) {
      return props.t("staffConsole.stripeImport.failure.temporary");
    }
    return props.t("staffConsole.stripeImport.failure.unknown");
  };
  // F148: `detail` is a SET of codes, so the card states every one of them.
  // A metadata purchase whose default-partner setting is also stale used to be
  // able to report only whichever fact won a precedence chain over one string.
  const eventDetailMessage = (detail: string | null): string[] => {
    const codes = importDetailCodes(detail);
    const messages: string[] = [];
    for (const code of codes) {
      const key = eventIssueKey(code);
      if (key && !messages.includes(key)) messages.push(key);
    }
    // A detail we could not classify is still a detail: the row says so rather
    // than going quiet, exactly as it did before F148.
    if (codes.length > 0 && messages.length === 0) messages.push("staffConsole.stripeImport.eventIssue.unknown");
    return messages.map((key) => props.t(key));
  };

  /**
   * F148: the configuration CHANNEL is a fact about the purchase, not an
   * issue, so it has its own quiet line. Only an event that really resolved a
   * configuration has one to state - a `no_match` never got that far.
   */
  const configSourceMessage = (event: EventRow): string | null => {
    if (event.outcome !== "imported" && event.outcome !== "switch_off") return null;
    return props.t(hasImportDetail(event.detail, DETAIL_CONFIG_METADATA)
      ? "staffConsole.stripeImport.configSource.metadata"
      : "staffConsole.stripeImport.configSource.mapping");
  };

  if (loadError) return <p className="staff-empty">{props.t("staffConsole.stripeImport.loadFailed")}</p>;
  if (!data) return <p className="staff-empty">{props.t("common.loading")}</p>;
  const visibleMappings = props.hideTestTenants
    ? data.mappings.filter((row) => !isTestLabel(row.label))
    : data.mappings;
  // Jobs carry the server verdict: asking here with an empty e-mail let four
  // `@example.cz` seeds through while the import queue on the SAME page hid
  // them, so two panels answered one question in opposite ways (audit S-04).
  const visibleJobs = props.hideTestTenants ? data.jobs.filter((job) => !job.is_test) : data.jobs;
  const visibleEvents = props.hideTestTenants
    ? data.events.filter((event) => !(event.is_test ?? isTestTenant(
      `${event.customer_name ?? ""} ${event.client_name ?? ""} ${event.matched_product_label ?? ""}`,
      event.customer_email ?? ""
    )))
    : data.events;

  return <div className="stripe-import-panel">
    {/* No lead here on purpose: the master switch states itself in bold two
        lines below, and a strip that repeats the next sentence is the same
        duplication S2-12 removed from the panel titles. */}
    <AdminReloadRow onClick={() => void reload()} t={props.t} />
    {notice && <p className="one-time-code">{notice}</p>}

    <form className="admin-tool-form" onSubmit={saveSettings}>
      <strong>{props.t("staffConsole.stripeImport.masterSwitch")}: {data.settings.enabled ? props.t("staffConsole.stripeImport.enabled") : props.t("staffConsole.stripeImport.disabled")}</strong>
      {/* A hint is text, not an empty state: `.staff-empty` is the dashed
          "there is nothing here" box (partner-view.css), and wearing it made
          the explanation read as a hole in the panel (audit S-18). */}
      <p className="admin-tool-hint">{data.settings.enabled ? props.t("staffConsole.stripeImport.enabledHint") : props.t("staffConsole.stripeImport.disabledHint")}</p>
      <label className="inline-fields">
        <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        <span>{props.t("staffConsole.stripeImport.enableLabel")}</span>
      </label>
      {/* An emptied required select explains itself (audit a3-05). */}
      {visiblePartners.length === 0 && (data?.partners.length ?? 0) > 0 ? (
        <div className="member-select-empty">
          <p className="admin-tool-hint">{props.t("staffConsole.team.allFiltered")}</p>
          <button className="secondary-button compact-button" type="button" onClick={() => props.onHideTestTenantsChange(false)}>
            {props.t("staffConsole.team.showTestMembers")}
          </button>
        </div>
      ) : (
        // S3-08: the label was written, it was simply never shown. A select
        // reading a person's name told nobody that THIS person inherits every
        // client Stripe creates.
        <label className="inline-select">
          <span>{props.t("staffConsole.stripeImport.defaultPartner")}</span>
          <select value={partnerId} onChange={(event) => setPartnerId(event.target.value)}>
            <option value="">{props.t("staffConsole.stripeImport.selectPartner")}</option>
            {visiblePartners.map((partner) => (
              <option key={partner.id} value={partner.id}>{`${partner.display_name ?? partner.email} · ${partner.email}`}</option>
            ))}
          </select>
        </label>
      )}
      {/* F168: an Empire buyer who already has a Buddy (free build) goes to
          THIS partner - David himself. Empty = the default partner rule. */}
      {visiblePartners.length > 0 && (
        <label className="inline-select">
          <span>{props.t("staffConsole.stripeImport.empireExistingPartner")}</span>
          <select value={empirePartnerId} onChange={(event) => setEmpirePartnerId(event.target.value)}>
            <option value="">{props.t("staffConsole.stripeImport.selectEmpireExistingPartner")}</option>
            {visiblePartners.map((partner) => (
              <option key={partner.id} value={partner.id}>{`${partner.display_name ?? partner.email} · ${partner.email}`}</option>
            ))}
          </select>
        </label>
      )}
      <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.empireExistingPartnerHint")}</p>
      {/* F171: every "Jen prodej" purchase goes to THIS partner (Tomas for
          now; he picks somebody else here). Empty = the default partner rule. */}
      {visiblePartners.length > 0 && (
        <label className="inline-select">
          <span>{props.t("staffConsole.stripeImport.salesOnlyPartner")}</span>
          <select value={salesOnlyPartnerId} onChange={(event) => setSalesOnlyPartnerId(event.target.value)}>
            <option value="">{props.t("staffConsole.stripeImport.selectSalesOnlyPartner")}</option>
            {visiblePartners.map((partner) => (
              <option key={partner.id} value={partner.id}>{`${partner.display_name ?? partner.email} · ${partner.email}`}</option>
            ))}
          </select>
        </label>
      )}
      <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.salesOnlyPartnerHint")}</p>
      <button className="primary-button" type="submit">{props.t("staffConsole.stripeImport.saveSettings")}</button>
    </form>

    <h4>{props.t("staffConsole.stripeImport.mappings")}</h4>
    {visibleMappings.length === 0 && <p className="staff-empty">{props.t("staffConsole.stripeImport.emptyMappings")}</p>}
    {visibleMappings.length > 0 && (
      <div className="stripe-card-grid">
        {visibleMappings.map((row) => (
          <article className="staff-card" key={row.id}>
            <div className="card-row">
              <strong>{row.label}</strong>
              <span>{row.active === 1 ? props.t("staffConsole.stripeImport.active") : props.t("staffConsole.stripeImport.inactive")}</span>
            </div>
            <p className="stripe-meta">{row.market_language === "cs" ? props.t("common.languageCs") : props.t("common.languageEn")}{row.includes_app === 1 ? ` · ${props.t("staffConsole.stripeImport.includesApp")}` : ""}</p>
            <button className="secondary-button compact-button" type="button" onClick={() => void toggleMapping(row)}>
              {row.active === 1 ? props.t("staffConsole.stripeImport.deactivate") : props.t("staffConsole.stripeImport.activate")}
            </button>
          </article>
        ))}
      </div>
    )}
    <form className="admin-tool-form" onSubmit={addMapping}>
      <label className="inline-select">
        <span>{props.t("staffConsole.stripeImport.productLabel")}</span>
        <select
          value={mapping.stripeProductId}
          onChange={(event) => {
            const product = visibleProducts?.find((item) => item.id === event.target.value);
            setMapping({ ...mapping, stripeProductId: event.target.value, label: product?.name ?? "" });
          }}
          disabled={!visibleProducts || visibleProducts.length === 0}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectProduct")}</option>
          {visibleProducts?.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
        </select>
      </label>
      <label className="inline-select">
        <span>{props.t("staffConsole.clientLocale")}</span>
        <select
          value={mapping.marketLanguage}
          onChange={(event) => setMapping({ ...mapping, marketLanguage: event.target.value })}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectLanguage")}</option>
          <option value="cs">{props.t("common.languageCs")}</option>
          <option value="en">{props.t("common.languageEn")}</option>
        </select>
      </label>
      <label className="inline-fields">
        <input type="checkbox" checked={mapping.includesApp} onChange={(event) => setMapping({ ...mapping, includesApp: event.target.checked })} />
        <span>{props.t("staffConsole.stripeImport.includesApp")}</span>
      </label>
      <div className="button-row">
        <button className="primary-button" type="submit" disabled={!mapping.stripeProductId || !mapping.marketLanguage}>{props.t("staffConsole.stripeImport.addMapping")}</button>
        <button className="secondary-button" type="button" onClick={() => void loadProducts()}>{props.t("staffConsole.stripeImport.loadProducts")}</button>
      </div>
      {/* S2-19: a dead control names its condition. A `title` never reaches
          the reader, because a disabled element takes no pointer. */}
      {!mapping.stripeProductId && (
        <p className="admin-tool-hint">
          {props.t(visibleProducts && visibleProducts.length > 0
            ? "staffConsole.stripeImport.addMappingNeedsProduct"
            : "staffConsole.stripeImport.addMappingNeedsLoad")}
        </p>
      )}
      {Boolean(mapping.stripeProductId) && !mapping.marketLanguage && (
        <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.addMappingNeedsLanguage")}</p>
      )}
      {productsUnavailable && <p className="staff-empty">{props.t("staffConsole.stripeImport.productsUnavailable")}</p>}
    </form>

    <h4>{props.t("staffConsole.stripeImport.manualImport")}</h4>
    <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.manualImportHint")}</p>
    <form className="admin-tool-form" onSubmit={importManually}>
      <label htmlFor="stripe-manual-name"><span>{props.t("staffConsole.stripeImport.manualName")}</span></label>
      <input
        id="stripe-manual-name"
        placeholder={props.t("staffConsole.stripeImport.manualName")}
        value={manual.name}
        onChange={(event) => setManual({ ...manual, name: event.target.value })}
        required
      />
      <label htmlFor="stripe-manual-email"><span>{props.t("staffConsole.stripeImport.manualEmail")}</span></label>
      <input
        id="stripe-manual-email"
        type="email"
        placeholder={props.t("staffConsole.stripeImport.manualEmail")}
        value={manual.email}
        onChange={(event) => setManual({ ...manual, email: event.target.value })}
        required
      />
      {/* S3-08: the same two fields the panel "Založit klienta" collects, with
          the same two words. Two panels doing one job had grown two grammars:
          there "Jazyk hovoru" and "Parťák" above their selects, here nothing
          above a select showing "Čeština". */}
      <label className="inline-select">
        <span>{props.t("staffConsole.clientLocale")}</span>
        <select
          value={manual.marketLanguage}
          onChange={(event) => setManual({ ...manual, marketLanguage: event.target.value })}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectLanguage")}</option>
          <option value="cs">{props.t("common.languageCs")}</option>
          <option value="en">{props.t("common.languageEn")}</option>
        </select>
      </label>
      {/* F152-W1 (D11): the same two options the admin creation form offers,
          from the same locale keys, so two panels doing one job cannot grow two
          vocabularies (the S3-08 law). Unchosen by default: see the state. */}
      <label className="inline-select" htmlFor="stripe-manual-journey">
        <span>{props.t("staffConsole.journeyMode")}</span>
        <select
          id="stripe-manual-journey"
          value={manual.journeyMode}
          onChange={(event) => setManual({ ...manual, journeyMode: event.target.value })}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectJourney")}</option>
          <option value="full">{props.t("staffConsole.journey.full")}</option>
          <option value="sales_only">{props.t("staffConsole.journey.sales_only")}</option>
        </select>
      </label>
      {visiblePartners.length === 0 && (data?.partners.length ?? 0) > 0 ? (
        <p className="admin-tool-hint">{props.t("staffConsole.team.allFiltered")}</p>
      ) : (
        <label className="inline-select">
          <span>{props.t("staffConsole.partner")}</span>
          <select value={manual.partnerId} onChange={(event) => setManual({ ...manual, partnerId: event.target.value })}>
            <option value="">{props.t("staffConsole.stripeImport.manualPartnerDefault")}</option>
            {visiblePartners.map((partner) => (
              <option key={partner.id} value={partner.id}>{`${partner.display_name ?? partner.email} · ${partner.email}`}</option>
            ))}
          </select>
        </label>
      )}
      <label className="inline-fields">
        <input type="checkbox" checked={manual.includesApp} onChange={(event) => setManual({ ...manual, includesApp: event.target.checked })} />
        <span>{props.t("staffConsole.stripeImport.includesApp")}</span>
      </label>
      <button className="primary-button" type="submit" disabled={!manual.marketLanguage || !manual.journeyMode}>
        {props.t("staffConsole.stripeImport.manualImportButton")}
      </button>
      {/* S2-19: a dead control names its condition beside itself - and names
          EACH missing field, so an operator who filled one of the two is not
          told to fill something they already did. */}
      {!manual.marketLanguage && (
        <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.addMappingNeedsLanguage")}</p>
      )}
      {!manual.journeyMode && (
        <p className="admin-tool-hint">{props.t("staffConsole.stripeImport.manualImportNeedsJourney")}</p>
      )}
    </form>

    <h4>{props.t("staffConsole.stripeImport.jobs")}</h4>
    <TestFilterNote shown={visibleJobs.length} hidden={data.jobs.length - visibleJobs.length} t={props.t}
      onShowTest={() => props.onHideTestTenantsChange(false)} />
    {visibleJobs.length === 0 && <p className="staff-empty">{props.t("staffConsole.stripeImport.emptyJobs")}</p>}
    {visibleJobs.length > 0 && (
      <div className="stripe-card-grid">
        {visibleJobs.map((job) => (
          <article className="staff-card" key={job.id}>
            <div className="card-row"><strong>{job.client_name}</strong><span>{jobStatusLabel(job.status)}</span></div>
            <small>{props.t("staffConsole.attempts")}: {job.attempts}</small>
            {researchFailureMessage(job.last_error) && <p className="stripe-status-detail">{researchFailureMessage(job.last_error)}</p>}
          </article>
        ))}
      </div>
    )}
    <form className="admin-tool-form" onSubmit={runResearch}>
      <label className="inline-select">
        <span>{props.t("staffConsole.stripeImport.clientLabel")}</span>
        <select
          value={researchClientId}
          onChange={(event) => setResearchClientId(event.target.value)}
          required
        >
          <option value="">{props.t("staffConsole.stripeImport.selectClient")}</option>
          {visibleClients.map((client) => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>
      </label>
      <button className="secondary-button" type="submit">{props.t("staffConsole.stripeImport.runResearch")}</button>
    </form>

    <h4>{props.t("staffConsole.stripeImport.events")}</h4>
    <TestFilterNote shown={visibleEvents.length} hidden={data.events.length - visibleEvents.length} t={props.t}
      onShowTest={() => props.onHideTestTenantsChange(false)} />
    {visibleEvents.length === 0 && <p className="staff-empty">{props.t("staffConsole.stripeImport.emptyEvents")}</p>}
    {visibleEvents.length > 0 && (
      <div className="stripe-card-grid">
        {visibleEvents.map((event) => (
          <article className="staff-card" key={event.id}>
            <div className="card-row">
              <strong>{event.customer_name ?? event.customer_email ?? "?"}</strong>
              <span>{outcomeLabel(event.outcome)}</span>
            </div>
            <p>{event.customer_email ?? ""}{event.matched_product_label ? ` · ${event.matched_product_label}` : ""}{event.client_name ? ` · ${event.client_name}` : ""}</p>
            <small>{formatWhen(event.created_at, props.locale)}</small>
            {configSourceMessage(event) && <p className="stripe-config-source">{configSourceMessage(event)}</p>}
            {eventDetailMessage(event.detail).map((message) => (
              <p className="stripe-status-detail" key={message}>{message}</p>
            ))}
          </article>
        ))}
      </div>
    )}
  </div>;
}

function formatWhen(value: string, locale: Locale): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "short", timeStyle: "short" }).format(date)
    : value;
}
